import { useEffect, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

import { Chip } from "@/components/ui/chip";
import { AMENITY_GROUPS, AMENITIES, FACETS, type AmenitySpec } from "@/lib/amenities";
import { cn } from "@/lib/utils";

// Renter-side amenity choice: the twelve facets renters filter on most, then
// an expander with every amenity under its category, the facets included
// (Oct 15: Ocean View was missing from Views), so a renter finds an amenity
// where they look for it. One selection: a facet checked in its category is
// checked on top too. Used by the browse filters, the profile preferences and
// the welcome quiz (through renderChip), so all three offer the same set.

/** Renter-facing amenities (pet types belong to the owner's Rules step). */
const ALL: readonly AmenitySpec[] = AMENITIES.filter((a) => !a.petType);
const MORE: readonly AmenitySpec[] = ALL.filter((a) => !a.filterable);

export interface AmenityPickerProps {
  /** Selected canonical labels. */
  selected: readonly string[];
  onToggle: (label: string) => void;
  /** Custom chip (e.g. PreferenceChip with a deal-breaker lock). Defaults to Chip. */
  renderChip?: (spec: AmenitySpec, checked: boolean, toggle: () => void) => ReactNode;
  className?: string;
  gridClassName?: string;
}

const AmenityPicker = ({ selected, onToggle, renderChip, className, gridClassName = "grid grid-cols-2 gap-1.5" }: AmenityPickerProps) => {
  const hasHiddenSelection = MORE.some((a) => selected.includes(a.label));
  const [open, setOpen] = useState(hasHiddenSelection);
  // A shared link or a saved profile can select something outside the facets;
  // never hide an active selection.
  useEffect(() => {
    if (hasHiddenSelection) setOpen(true);
  }, [hasHiddenSelection]);

  const chip = (spec: AmenitySpec) => {
    const checked = selected.includes(spec.label);
    const toggle = () => onToggle(spec.label);
    if (renderChip) return <span key={spec.key} className="contents">{renderChip(spec, checked, toggle)}</span>;
    return <Chip key={spec.key} label={spec.label} checked={checked} onChange={toggle} />;
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className={gridClassName}>{FACETS.map(chip)}</div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground underline underline-offset-4"
      >
        {open ? "Fewer amenities" : "All amenities by category"}
        <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} aria-hidden />
      </button>
      {open && (
        <div className="space-y-3 pt-1">
          {AMENITY_GROUPS.map((g) => {
            const items = ALL.filter((a) => a.group === g.id);
            if (!items.length) return null;
            return (
              <div key={g.id} role="group" aria-label={g.label}>
                <div className="mb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground" aria-hidden>{g.label}</div>
                <div className={gridClassName}>{items.map(chip)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AmenityPicker;
